const sections = [
  {
    title: 'Data we collect',
    body: [
      'Account and sign-in information, such as your name, email address, team membership, authentication status, and session identifiers needed to keep you signed in.',
      'LinkedIn messaging data, including conversations, message text, attachments, profile identifiers, names, headlines, connection status, and invitation activity.',
      'CRM integration data, when enabled, including Affinity connection status, CRM contact identifiers, CRM matching data, and message or activity sync metadata.',
      'Local extension data, such as sync cursors, cached conversation data, preferences, and temporary state needed to keep the extension working.',
      'Basic product telemetry and error information used to diagnose bugs and improve reliability.',
    ],
  },
  {
    title: 'How we use data',
    body: [
      'Syncing LinkedIn conversations and invitations into Slash.',
      'Displaying, searching, organizing, and managing LinkedIn messages.',
      'Sending user-initiated LinkedIn actions, such as messages or invitation actions.',
      'Supporting optional CRM workflows, such as matching LinkedIn contacts to Affinity records and syncing selected activity.',
      'Maintaining account, team, security, audit, and reliability features.',
    ],
  },
  {
    title: 'Chrome extension permissions',
    body: [
      'Cookies are used for LinkedIn session-backed requests on LinkedIn domains.',
      'Storage is used to store local extension state and sync metadata.',
      'Alarms are used for background sync scheduling.',
      'Tabs and scripting are used to detect supported pages and inject packaged Slash scripts.',
      'WebRequest is used for supported LinkedIn messaging and invitation sync flows.',
      'Host permissions are limited to LinkedIn, LinkedIn media, Slash-owned domains, and supported CRM APIs.',
    ],
  },
]

export default function Privacy() {
  return (
    <main className="min-h-svh bg-white text-zinc-900">
      <div className="mx-auto flex max-w-3xl flex-col gap-12 px-6 py-16 sm:px-8 lg:px-0">
        <header className="flex flex-col gap-5">
          <a href="/" className="text-2xl font-bold tracking-tight text-zinc-950">/slash</a>
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-zinc-400">
              Privacy Policy
            </p>
            <h1 className="text-4xl font-semibold tracking-tight text-zinc-950 sm:text-5xl">
              Slash Privacy Policy
            </h1>
            <p className="text-sm text-zinc-500">Last updated: April 23, 2026</p>
          </div>
          <p className="max-w-2xl text-lg leading-8 text-zinc-600">
            Slash is a LinkedIn messaging workflow tool. This policy explains what data
            the Slash Chrome extension and Slash web app collect and how that data is used.
          </p>
        </header>

        <section className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
          <h2 className="text-lg font-semibold text-zinc-950">Single purpose</h2>
          <p className="mt-4 text-sm leading-6 text-zinc-600">
            Slash&apos;s single purpose is to help users manage LinkedIn conversations,
            invitations, and related CRM workflows from the Slash web app and Chrome
            extension.
          </p>
        </section>

        <section className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
          <h2 className="text-lg font-semibold text-zinc-950">Data sharing</h2>
          <div className="mt-4 flex flex-col gap-3 text-sm leading-6 text-zinc-600">
            <p>We do not sell user data.</p>
            <p>
              We do not use or transfer user data for purposes unrelated to Slash&apos;s
              core functionality.
            </p>
            <p>
              We do not use or transfer user data to determine creditworthiness or for
              lending purposes.
            </p>
            <p>
              Data may be processed by service providers needed to operate Slash, such as
              hosting, database, authentication, error monitoring, analytics, email delivery,
              and CRM APIs that users choose to connect.
            </p>
          </div>
        </section>

        <div className="flex flex-col gap-10">
          {sections.map((section) => (
            <section key={section.title} className="flex flex-col gap-4">
              <h2 className="text-xl font-semibold tracking-tight text-zinc-950">
                {section.title}
              </h2>
              <ul className="flex list-disc flex-col gap-2 pl-5 text-sm leading-6 text-zinc-600">
                {section.body.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="flex flex-col gap-4 border-t border-zinc-200 pt-10">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-950">
            Remote code
          </h2>
          <p className="text-sm leading-6 text-zinc-600">
            Slash does not execute remotely hosted code. Extension code is packaged with
            the extension. Network responses from LinkedIn, Slash, and Affinity are used
            as data only and are not executed as code.
          </p>
        </section>

        <section className="flex flex-col gap-4 border-t border-zinc-200 pt-10">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-950">
            Data retention and deletion
          </h2>
          <p className="text-sm leading-6 text-zinc-600">
            Users may request deletion of their Slash account and associated data by
            contacting support. Local browser extension data can also be cleared by
            removing the extension or clearing the extension&apos;s local data in the
            browser.
          </p>
        </section>

        <section className="flex flex-col gap-4 border-t border-zinc-200 pt-10">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-950">Security</h2>
          <p className="text-sm leading-6 text-zinc-600">
            Slash uses separate production and staging environments, HTTPS, Supabase
            row-level security, and access controls designed to protect user and team data.
          </p>
        </section>

        <section className="flex flex-col gap-4 border-t border-zinc-200 pt-10">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-950">Contact</h2>
          <p className="text-sm leading-6 text-zinc-600">
            For privacy questions or deletion requests, contact{' '}
            <a className="font-medium text-zinc-950 underline" href="mailto:support@fwdslash.dev">
              support@fwdslash.dev
            </a>
            .
          </p>
        </section>
      </div>
    </main>
  )
}
